import { useMemo } from 'react';
import type { InternalTableSchema } from '../core/schema/types';
import { evaluateCondition } from '../utils/evaluateCondition';
import { useGroupedData, type GroupedRow } from '../core/engine/useGroupedData';
import { isRowSelectable } from '../utils/rowSelection';

interface UseTableDataPipelineParams {
  displayData: unknown[];
  tableState: {
    sort: { field: string; direction: 'asc' | 'desc' } | null;
    pagination: { pageIndex: number; pageSize: number };
    expandedGroups: Set<string>;
  };
  internalSchema: InternalTableSchema;
  resolveRowKey: (row: Record<string, unknown>, rowIndex: number) => string;
  visibleColumns: ReadonlyArray<{ field: string; header: string }>;
}

export interface UseTableDataPipelineReturn {
  sortedDisplayData: unknown[];
  paginatedData: unknown[];
  topPinnedRows: unknown[];
  centerRows: unknown[];
  bottomPinnedRows: unknown[];
  isGroupingEnabled: boolean;
  groupByConfig: InternalTableSchema['features']['groupBy'];
  groupExpandMode: string;
  groupAlwaysExpanded: boolean;
  groupedData: ReadonlyArray<Record<string, unknown> | GroupedRow>;
  groupKeys: string[];
  renderData: ReadonlyArray<Record<string, unknown> | GroupedRow>;
  selectableKeys: string[];
}

export function useTableDataPipeline({
  displayData,
  tableState,
  internalSchema,
  resolveRowKey,
  visibleColumns,
}: UseTableDataPipelineParams): UseTableDataPipelineReturn {
  const sortedDisplayData = useMemo(() => {
    if (!tableState.sort) return displayData;

    const mode = internalSchema.features.pagination.mode;
    const hasRemoteData = internalSchema.dataSource != null && internalSchema.dataSource.endpoint.length > 0;
    const isServerDrivenSort =
      hasRemoteData &&
      mode !== 'clientSide' &&
      mode !== 'none';
    if (isServerDrivenSort) return displayData;

    const { field, direction } = tableState.sort;
    const multiplier = direction === 'asc' ? 1 : -1;
    return [...displayData].sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[field];
      const bVal = (b as Record<string, unknown>)[field];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * multiplier;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * multiplier;
      }
      return String(aVal).localeCompare(String(bVal)) * multiplier;
    });
  }, [displayData, tableState.sort, internalSchema.features.pagination.mode, internalSchema.dataSource]);

  const paginatedData = useMemo(() => {
    const mode = internalSchema.features.pagination.mode;
    if (mode === 'clientSide') {
      const { pageIndex, pageSize } = tableState.pagination;
      const start = pageIndex * pageSize;
      return sortedDisplayData.slice(start, start + pageSize);
    }
    return sortedDisplayData;
  }, [sortedDisplayData, internalSchema.features.pagination.mode, tableState.pagination]);

  const rowPinning = internalSchema.features.rowPinning;
  const { topPinnedRows, centerRows, bottomPinnedRows } = useMemo(() => {
    if (!rowPinning)
      return {
        topPinnedRows: [] as unknown[],
        centerRows: paginatedData,
        bottomPinnedRows: [] as unknown[],
      };
    const top: unknown[] = [];
    const center: unknown[] = [];
    const bottom: unknown[] = [];
    for (const row of paginatedData) {
      const rec = row as Record<string, unknown>;
      if (rowPinning.top && evaluateCondition(rowPinning.top, rec)) top.push(row);
      else if (rowPinning.bottom && evaluateCondition(rowPinning.bottom, rec)) bottom.push(row);
      else center.push(row);
    }
    return { topPinnedRows: top, centerRows: center, bottomPinnedRows: bottom };
  }, [paginatedData, rowPinning]);

  const groupByConfig = internalSchema.features.groupBy;
  const isGroupingEnabled = groupByConfig != null && groupByConfig.fields.length > 0;
  const groupByFields: ReadonlyArray<string> = groupByConfig?.fields ?? [];
  const groupExpandMode = groupByConfig?.expandMode ?? 'collapsed';
  const groupAlwaysExpanded = groupExpandMode === 'alwaysExpanded';

  const { groupedData, groupKeys } = useGroupedData({
    data: centerRows,
    groupByFields,
    expandedGroups: groupAlwaysExpanded
      ? new Set([] as string[])
      : tableState.expandedGroups,
    columns: visibleColumns as any,
    defaultExpanded: groupAlwaysExpanded || groupExpandMode === 'expanded',
    hideGroupedColumn: groupByConfig?.hideGroupedColumn ?? false,
  });

  const renderData: ReadonlyArray<Record<string, unknown> | GroupedRow> =
    isGroupingEnabled ? groupedData : (centerRows as ReadonlyArray<Record<string, unknown>>);

  const selection = internalSchema.features.selection;
  const selectableKeys = useMemo(() => {
    return displayData
      .map((r, i) => {
        const rec = r as Record<string, unknown>;
        const key = resolveRowKey(rec, i);
        const canSelect = isRowSelectable(rec, selection.selectable);
        return canSelect ? key : null;
      })
      .filter((k): k is string => k !== null);
  }, [displayData, resolveRowKey, selection.selectable]);

  return {
    sortedDisplayData,
    paginatedData,
    topPinnedRows,
    centerRows,
    bottomPinnedRows,
    isGroupingEnabled,
    groupByConfig,
    groupExpandMode,
    groupAlwaysExpanded,
    groupedData,
    groupKeys,
    renderData,
    selectableKeys,
  };
}
